<template>
    <div>
        <b-alert :show="hasError" variant="danger">Error loading acceptance records.</b-alert>
        <b-table :items="filteredRecords" :fields="fields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
            :current-page="pageNumber" :per-page="pageSize">
            <template #cell(acceptanceConfiguration.name)="data">
                <strong>
                    <a v-if="hasForm(data.item)" href="#"
                        @click.prevent="viewForm(data.item)" title="View Document"
                        :class="{ 'text-muted': loadingFormRecordId === data.item.id }">
                        {{ data.value }}
                        <b-spinner v-if="loadingFormRecordId === data.item.id" small class="ml-1" />
                        <b-icon v-else icon="file-pdf" variant="primary" class="ml-1" />
                    </a>
                    <span v-else>{{ data.value }}</span>
                </strong>
            </template>
            <template #cell(signatureData)="data">
                {{ data.value ? "✅" : "❌" }}
            </template>
            <template #cell(orderId)="data">
                <strong>
                    <RouterLink :to="{ name: 'OrderDetails', params: { orderId: data.value } }">
                        {{ data.value }}
                    </RouterLink>
                </strong>
            </template>
            <template #cell(created)="data">
                {{ data.value | formatDate("LLL") }}
                <div>
                    <small>{{ data.item.createdBy }}</small>
                </div>
            </template>
            <template #cell(endDate)="data">
                {{ data.value | formatLocalDate("LL") }}
            </template>
            <template #cell(actions)="data">
                <div class="table-icon-actions">
                    <b-button v-if="canViewFormWithSignature(data.item)" variant="link"
                        title="Fax" @click.prevent="openFaxModal(data.item)"
                        :disabled="loadingFormRecordId === data.item.id">
                        <b-icon icon="printer" variant="info" />
                    </b-button>
                    <b-button variant="link" title="Edit" @click.prevent="startEdit(data.item)">
                        <b-icon icon="pencil" variant="primary" />
                    </b-button>
                </div>
            </template>
            <template #cell(declined)="{ value }">
                <b-badge v-if="value" variant="danger">Declined</b-badge>
            </template>
            <template #cell(sourceType)="{ value }">
                <b-badge :variant="getSourceVariant(value)">
                    {{ getSourceLabel(value) }}
                </b-badge>
            </template>
        </b-table>

        <BottomPagerBar v-model="pageNumber" :total-rows="filteredRecords.length" :per-page="pageSize"
            show-page-size-selector :loading="loading" @change-page-size="pageSize = $event" />

        <!-- Add New Acceptance Modal -->
        <b-modal :visible="showAddModal" title="Add Acceptance Record" ok-title="Create Acceptance"
            :ok-disabled="!newAcceptance.configurationId || uploadingDocument"
            @hidden="closeAddModal" @ok="createNewAcceptance" centered no-fade>
            <b-form-group label="Acceptance Configuration *">
                <SearchComponent v-model="selectedConfig" search-u-r-l="/AcceptanceConfiguration"
                    :option-label="configLabel" :prepopulate="true"
                    :results-modifier="filterActiveConfigs"
                    label="" placeholder="Search for acceptance configuration..." />
            </b-form-group>

            <b-form-group label="Source Type *">
                <b-select v-model="newAcceptance.sourceType" :options="sourceTypeOptions" />
            </b-form-group>

            <b-form-group label="Status *">
                <b-form-radio-group v-model="newAcceptance.declined" :options="statusOptions" />
            </b-form-group>

            <b-form-group v-if="newAcceptance.sourceType === AcceptanceSource.Written" label="Upload Document (Optional)">
                <b-form-file
                    v-model="documentFile"
                    accept=".pdf"
                    placeholder="Choose PDF document or drop it here..."
                    drop-placeholder="Drop file here...">
                </b-form-file>
            </b-form-group>

            <div v-if="uploadingDocument">
                <b-spinner small class="mr-2" />
                <span>Uploading document...</span>
            </div>
        </b-modal>

        <!-- Edit Acceptance Modal -->
        <b-modal :visible="!!selectedRecord" centered no-fade ok-title="Save"
            @hidden="selectedRecord = null" @ok="saveRecord">
            <template #modal-header="{ close }">
                <h5 class="modal-title">Edit Acceptance Record</h5>
                <div class="ml-auto d-flex flex-row align-items-center">
                    <div class="d-flex align-items-center" style="margin-right: 20px;">
                        <history-modal v-if="selectedRecord?.id" v-model="selectedRecord" />
                    </div>
                    <div class="d-flex align-items-center">
                        <button type="button" class="close" @click="close" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                </div>
            </template>
            <template v-if="selectedRecord">
                <b-form-group label="Acceptance Configuration">
                    <b-input :value="selectedRecord.acceptanceConfiguration.name" readonly />
                </b-form-group>
                <b-form-group label="Has Signature Data">
                    <b-input :value="selectedRecord.signatureData ? '✅' : '❌'" readonly />
                </b-form-group>
                <b-form-group label="Order Id">
                    <b-input :value="selectedRecord.orderId" readonly />
                </b-form-group>
                <b-form-group label="Source Type">
                    <b-select v-model="selectedRecord.sourceType" :options="sourceTypeOptions" />
                </b-form-group>
                <b-form-group label="End Date">
                    <el-date-picker v-model="selectedRecord.endDate" format="MM/dd/yyyy" type="date" today-button
                        reset-button :picker-options="datePickerOptions" />
                </b-form-group>
                <div>
                    <b-check v-model="selectedRecord.declined" switch>Declined</b-check>
                </div>
            </template>
        </b-modal>

        <!-- Fax Acceptance Form Modal -->
        <b-modal :visible="!!faxRecord" title="Fax Acceptance Form" centered no-fade
            :ok-title="sendingFax ? 'Sending Fax...' : 'Send Fax'" :ok-disabled="!isFaxNumberValid || sendingFax || loadingFaxNumbers"
            @hidden="closeFaxModal" @ok="sendFax">
            <template v-if="faxRecord">
                <div v-if="loadingFaxNumbers" class="text-center my-3">
                    <b-spinner small class="mr-2" />
                    <span>Loading fax numbers...</span>
                </div>
                <template v-else>
                    <b-form-group v-if="availableFaxNumbers.length > 0" label="Fax Number *">
                        <b-form-select v-model="selectedFaxOption" :disabled="sendingFax"
                            @change="onFaxOptionChange">
                            <b-form-select-option v-for="opt in availableFaxNumbers" :key="opt.faxNumber"
                                :value="opt.faxNumber">
                                {{ opt.label }} - {{ opt.faxNumber }}
                            </b-form-select-option>
                            <b-form-select-option value="custom">Custom Fax Number</b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                    <b-form-group v-if="availableFaxNumbers.length === 0 || selectedFaxOption === 'custom'"
                        :label="availableFaxNumbers.length > 0 ? 'Custom Fax Number *' : 'Fax Number *'"
                        description="e.g., 8005551234, 18005551234, or +18005551234">
                        <b-input v-model="faxNumber" placeholder="Enter fax number..." :disabled="sendingFax"
                            :state="faxNumber ? isFaxNumberValid : null" />
                    </b-form-group>
                </template>
                <b-form-group label="Note (Optional)">
                    <b-form-textarea v-model="faxNote" placeholder="Optional cover page note..."
                        :disabled="sendingFax" rows="3" maxlength="2000" />
                </b-form-group>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { AcceptanceRecord } from '@/models/AcceptanceRecord';
import { AcceptanceConfiguration, AcceptanceConfigurationStatus } from '@/models/AcceptanceConfiguration';
import { AcceptanceSource, getAcceptanceSourceLabel, getAcceptanceSourceBadgeVariant, getSourceTypeOptions } from '@/models/AcceptanceSource';
import { PatientDocument } from '@/models/PatientDocument';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import BottomPagerBar from './BottomPagerBar.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import { DatePicker } from "element-ui";
// Element UI DatePickerOptions type - defined inline
interface DatePickerOptions {
  disabledDate?: (date: Date) => boolean;
}
import axios from 'axios';
import moment from 'moment';
import { toRaw } from 'vue';
import { BvModalEvent } from 'bootstrap-vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import HistoryModal from '@/components/Modals/HistoryModal.vue';

@Component({
    name: "ConsultationListComponent",
    components: {
        BottomPagerBar,
        SearchComponent,
        [DatePicker.name]: DatePicker,
        HistoryModal,
    },
})
export default class AcceptanceRecordList extends Vue {
    @Prop({ default: null }) protected patientId!: number;

    protected records: Array<AcceptanceRecord> = [];
    protected pageNumber = 1;
    protected pageSize = 5;
    protected loading = false;
    protected hasError = false;

    protected sortBy = "created";
    protected sortDesc = true;

    protected selectedRecord: AcceptanceRecord | null = null;
    protected showAddModal = false;
    protected selectedConfig: AcceptanceConfiguration | null = null;
    protected documentFile: File | null = null;
    protected uploadingDocument = false;
    protected AcceptanceSource = AcceptanceSource;
    protected newAcceptance = {
        configurationId: 0,
        sourceType: AcceptanceSource.Written,
        declined: false,
        imageId: null as number | null
    };

    protected faxRecord: AcceptanceRecord | null = null;
    protected faxNumber = '';
    protected faxNote = '';
    protected sendingFax = false;
    protected availableFaxNumbers: Array<{ label: string; faxNumber: string }> = [];
    protected loadingFaxNumbers = false;
    protected selectedFaxOption = '';
    protected loadingFormRecordId: number | null = null;

    mounted() {
        this.pageSize = getPageSizePreference(this, 'pageSize_AcceptanceRecords', 5);
        this.getRecords();
    }

    @Watch("pageSize")
    onPageSizePreferenceChanged() {
        savePageSizePreference(this, 'pageSize_AcceptanceRecords', this.pageSize);
    }

    @Watch("pageSize")
    @Watch("sortBy")
    @Watch("sortDesc")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    @Watch("patientId")
    async getRecords() {
        if (!this.patientId) {
            this.records = [];
            return;
        }
        try {
            this.loading = true;
            this.hasError = false;
            const resp = await this.$http.get<AcceptanceRecord[]>("AcceptanceRecord", { params: { patientId: this.patientId } });
            this.records = resp.data;
        } catch (err) {
            console.error(err);
            this.hasError = true;
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    get fields() {
        return [
            "id",
            {
                key: "acceptanceConfiguration.name",
                label: "Acceptance Configuration",
                sortable: true,
            },
            {
                key: "sourceType",
                label: "Source",
                sortable: true,
            },
            {
                key: "signatureData",
                label: "Has Signature Data",
            },
            {
                key: "orderId",
                label: "Order Id",
                sortable: true,
            },
            "endDate",
            { key: "declined" },
            {
                key: "created",
                sortable: true,
            },
            {
                key: "actions",
                label: "",
            },
        ];
    }

    get filteredRecords() {
        return this.records;
    }

    get sourceTypeOptions() {
        return getSourceTypeOptions();
    }

    get statusOptions() {
        return [
            { value: false, text: 'Accepted' },
            { value: true, text: 'Declined' }
        ];
    }

    @Watch('selectedConfig')
    onConfigSelected(config: AcceptanceConfiguration | null) {
        this.newAcceptance.configurationId = config?.id || 0;
    }

    protected getSourceLabel(source: AcceptanceSource): string {
        return getAcceptanceSourceLabel(source);
    }

    protected getSourceVariant(source: AcceptanceSource): string {
        return getAcceptanceSourceBadgeVariant(source);
    }

    protected async viewDocument(imageId: number) {
        try {
            const res = await this.$http.get(`/api/Image/${imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
            setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        } catch (err) {
            console.error("Error viewing document", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    protected canViewFormWithSignature(record: AcceptanceRecord): boolean {
        return !!record.signatureData && !!record.acceptanceConfiguration?.imageId;
    }

    protected hasForm(record: AcceptanceRecord): boolean {
        return !!record.acceptanceConfiguration?.imageId;
    }

    protected async viewForm(record: AcceptanceRecord) {
        if (this.loadingFormRecordId) return;
        try {
            this.loadingFormRecordId = record.id;
            if (record.signatureData && record.acceptanceConfiguration?.imageId) {
                await this.viewFormWithSignature(record);
            } else if (record.acceptanceConfiguration?.imageId) {
                await this.viewDocument(record.acceptanceConfiguration.imageId);
            }
        } finally {
            this.loadingFormRecordId = null;
        }
    }

    protected async viewFormWithSignature(record: AcceptanceRecord) {
        try {
            const res = await this.$http.get(`/api/AcceptanceRecord/${record.id}/FormWithSignature`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
            setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        } catch (err) {
            console.error("Error viewing form with signature", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    datePickerOptions: DatePickerOptions = {
        disabledDate(date: any) {
            return moment(date).isBefore(moment().subtract(1, "days"));
        },
    };

    startEdit(record: AcceptanceRecord) {
        this.selectedRecord = new AcceptanceRecord(structuredClone(toRaw(record)));
    }

    async saveRecord(e: BvModalEvent) {
        e.preventDefault();
        try {
            await this.$http.post("AcceptanceRecord", this.selectedRecord);
            await this.getRecords();
            this.selectedRecord = null;
            this.$emit("update");
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    public openAddModal() {
        this.showAddModal = true;
    }

    protected configLabel(config: AcceptanceConfiguration): string {
        return config.name || '';
    }

    protected filterActiveConfigs(configs: AcceptanceConfiguration[]): AcceptanceConfiguration[] {
        return configs.filter(c => c.status === AcceptanceConfigurationStatus.Active);
    }

    closeAddModal() {
        this.showAddModal = false;
        this.selectedConfig = null;
        this.documentFile = null;
        this.uploadingDocument = false;
        this.newAcceptance = {
            configurationId: 0,
            sourceType: AcceptanceSource.Written,
            declined: false,
            imageId: null
        };
    }

    protected async openFaxModal(record: AcceptanceRecord) {
        this.faxRecord = record;
        this.faxNumber = '';
        this.faxNote = '';
        this.availableFaxNumbers = [];
        this.selectedFaxOption = '';
        this.loadingFaxNumbers = true;

        try {
            const { data } = await this.$http.get(`/api/AcceptanceRecord/${record.id}/AvailableFaxNumbers`);
            this.availableFaxNumbers = data.filter((f: any) => this.isValidFaxNumber(f.faxNumber));
            if (data.length > 0) {
                this.selectedFaxOption = data[0].faxNumber;
                this.faxNumber = data[0].faxNumber;
            }
        } catch {
            // Silently fall back to manual entry
        } finally {
            this.loadingFaxNumbers = false;
        }
    }

    protected closeFaxModal() {
        this.faxRecord = null;
        this.faxNumber = '';
        this.faxNote = '';
        this.sendingFax = false;
        this.availableFaxNumbers = [];
        this.loadingFaxNumbers = false;
        this.selectedFaxOption = '';
    }

    protected onFaxOptionChange(value: string) {
        if (value === 'custom') {
            this.faxNumber = '';
        } else {
            this.faxNumber = value;
        }
    }

    private isValidFaxNumber(number: string): boolean {
        if (!number) return false;
        const digits = number.replace(/\D/g, '');
        return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
    }

    get isFaxNumberValid(): boolean {
        return this.isValidFaxNumber(this.faxNumber);
    }

    protected async sendFax(e: BvModalEvent) {
        e.preventDefault();
        if (!this.faxRecord || !this.faxNumber) return;

        try {
            this.sendingFax = true;
            await this.$http.post(`/api/AcceptanceRecord/${this.faxRecord.id}/Fax`, {
                faxNumber: this.faxNumber,
                note: this.faxNote || null,
            }, { timeout: 35000 });
            this.$notification(NotificationOptions.success("Fax sent successfully"));
            this.closeFaxModal();
        } catch (err) {
            console.error("Error sending fax", err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.sendingFax = false;
        }
    }

    async createNewAcceptance(e: BvModalEvent) {
        e.preventDefault();

        if (!this.newAcceptance.configurationId || !this.patientId) {
            this.$notification(NotificationOptions.error("Please select an acceptance configuration"));
            return;
        }

        try {
            let imageId: number | null = null;

            // Upload document first if provided
            if (this.documentFile && this.newAcceptance.sourceType === AcceptanceSource.Written) {
                this.uploadingDocument = true;
                const formData = new FormData();
                formData.append('document', this.documentFile);
                formData.append('fileName', `Acceptance - ${this.selectedConfig!.name}`);

                const uploadResp = await axios.post<PatientDocument>(
                    `/patient/${this.patientId}/add-document/`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                imageId = uploadResp.data.imageId ?? null;
            }

            // Create acceptance record
            await axios.post('AcceptanceRecord/with-document', {
                configurationId: this.newAcceptance.configurationId,
                patientId: this.patientId,
                imageId: imageId,
                sourceType: this.newAcceptance.sourceType,
                declined: this.newAcceptance.declined
            });

            this.$notification(NotificationOptions.success("Acceptance record created successfully"));
            await this.getRecords();
            this.closeAddModal();
            this.$emit("update");
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.uploadingDocument = false;
        }
    }
}

</script>
